const CACHE_HEADER = "x-billsarthi-desktop-cache";
const API_HOSTS = new Set(["qr.billsarthi.in"]);

const isDesktop = () =>
  typeof window !== "undefined" && Boolean(window.desktopApiCache);

const getRequestMethod = (input, init) =>
  String(init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();

const getRequestUrl = (input) =>
  typeof input === "string" || input instanceof URL
    ? String(input)
    : input?.url || "";

const readBody = async (input, init) => {
  if (typeof init?.body === "string") return init.body;
  if (input instanceof Request && !init?.body) return input.clone().text();
  return "";
};

const isReadEndpoint = (url, method, body) => {
  if (!API_HOSTS.has(url.hostname) || !url.pathname.includes("/public/api/")) return false;
  if (method === "GET") return true;
  if (method !== "POST" || body instanceof FormData) return false;

  // POST is used by the existing API for reads too. Keep mutations out of cache.
  return /\/(list|get|report(?:-v2)?|summary|history|pricing|categories|entitlement-status|checkvalid|monthsummary|product\/variants\/list|product\/addons\/(list|map|by-product)|waiter\/kot\/history|referral\/info)(?:\/|$|\?)/i.test(
    url.pathname
  );
};

const getScope = (body) => {
  try {
    const parsed = JSON.parse(body || "{}");
    if (parsed?.restaurantid || parsed?.restaurantId) {
      return `restaurant:${parsed.restaurantid || parsed.restaurantId}`;
    }
  } catch (_) {}

  try {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (user?.id) return `restaurant:${user.id}`;
  } catch (_) {}
  return "anonymous";
};

const makeKey = ({ url, method, body, scope }) =>
  `${scope}|${method}|${url}|${body || ""}`;

const toCachedResponse = (entry) =>
  new Response(entry.body, {
    status: entry.status || 200,
    headers: {
      "content-type": entry.contentType || "application/json",
      [CACHE_HEADER]: "1",
    },
  });

const storeResponse = async (cacheKey, response) => {
  if (!response?.ok) return;
  const contentType = response.headers.get("content-type") || "";
  if (!/application\/json|text\/json/i.test(contentType)) return;
  const body = await response.clone().text();
  if (!body.trim()) return;
  await window.desktopApiCache.set({
    key: cacheKey,
    body,
    status: response.status,
    contentType,
  });
};

/**
 * Adds cache-first, stale-while-revalidate behavior for existing read APIs.
 * It is installed only in Electron; the normal browser keeps native fetch.
 */
export const installElectronApiCache = () => {
  if (!isDesktop() || window.__billsArthiApiCacheInstalled) return;
  window.__billsArthiApiCacheInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const rawUrl = getRequestUrl(input);
    let url;
    try {
      url = new URL(rawUrl, window.location.href);
    } catch (_) {
      return nativeFetch(input, init);
    }

    const method = getRequestMethod(input, init);
    const body = await readBody(input, init);
    if (!isReadEndpoint(url, method, init?.body)) return nativeFetch(input, init);

    const cacheKey = makeKey({ url: url.toString(), method, body, scope: getScope(body) });
    try {
      const cached = await window.desktopApiCache.get({ key: cacheKey });
      if (cached?.body) {
        // Do not block UI on the network. The next screen load receives refreshed data.
        nativeFetch(input, init)
          .then((response) => storeResponse(cacheKey, response))
          .catch(() => {});
        return toCachedResponse(cached);
      }
    } catch (error) {
      console.warn("Unable to read Electron API cache", error);
    }

    const response = await nativeFetch(input, init);
    storeResponse(cacheKey, response).catch(() => {});
    return response;
  };
};
